import { Box, Divider, HStack, IBoxProps, Pressable, VStack } from 'native-base';
import { get, isEmpty } from 'lodash';

import { Expand01Icon, Text } from '@pimm/base';
import { StoreReportEntry } from '../hooks/combine-store-reports.hook';

export type TableCell = {
  cellProps?: IBoxProps;
  dataField?: string; // keyof StoreReportDaySummary;
  header?: string;
  hidden?: boolean;
  renderCell?: (_row: { entry: StoreReportEntry; isDisabled: boolean; level: number }) => React.ReactNode | string;
  renderHeader?: (_row: { entry: StoreReportEntry; level: number }) => React.ReactNode | string;
};

type StoreReportTableRowProps = {
  columns: TableCell[];
  index: number;
  isActive?: boolean;
  isDisabled?: boolean;
  entry: StoreReportEntry;
  level?: number;
  size?: 'md' | 'xl';
  onPress?: () => void;
};

export const StoreReportTableRow = ({
  columns,
  index: rowIndex,
  isActive,
  isDisabled,
  entry,
  level = 1,
  size = 'xl',
  ..._props
}: StoreReportTableRowProps) => {
  const isFirstLevel = level === 1;
  return (
    <VStack>
      <Pressable
        outlineStyle="none"
        isDisabled={isDisabled || level > 1}
        _hover={{ bg: !isDisabled ? 'gray.50' : undefined }}
        onPress={_props.onPress}
      >
        {({ isHovered }) => (
          <HStack
            alignItems="center"
            px={1}
            borderTopWidth={rowIndex ? 1 : 0}
            borderColor={isFirstLevel ? undefined : 'gray.50'}
            bg={isActive ? 'primary.25' : undefined}
          >
            {/* Highlight Marker */}
            {!isDisabled && (isHovered || isActive) && isFirstLevel && (
              <Divider orientation="vertical" position="absolute" left={0} h="full" w="5px" bg="primary.600" />
            )}

            {size === 'xl' && (
              <Box alignItems="center" justifyContent="center" minH={isFirstLevel ? 10 : 5} w={8} opacity={isDisabled ? 0.5 : 1}>
                {isFirstLevel && <Expand01Icon size="14px" color="gray.500" />}
              </Box>
            )}

            {columns.map((_column, colIndex) => {
              let cell = get(entry, _column.dataField ?? '') ?? '-';

              if (_column.hidden) return null;
              if (_column.renderCell) cell = _column.renderCell({ entry, isDisabled: !!isDisabled, level });

              return (
                <Box
                  key={`store-reports-table.${entry.id}.${colIndex}`}
                  flex={1}
                  justifyContent="center"
                  minHeight={isFirstLevel ? 10 : 9}
                  opacity={isDisabled ? 0.5 : 1}
                  py={isFirstLevel ? 1.5 : 1}
                  px={2}
                  {..._column.cellProps}
                >
                  {typeof cell === 'string' || typeof cell === 'number' ? (
                    <Text size="md" fontWeight={400} color="gray.900">
                      {cell}
                    </Text>
                  ) : (
                    cell
                  )}
                </Box>
              );
            })}
          </HStack>
        )}
      </Pressable>

      {size === 'xl' && !isEmpty(entry.entries) && (
        <Box bg="gray.25" borderTopWidth={1}>
          {entry.entries!.map((_entry, index) => (
            <StoreReportTableRow
              key={`${entry.id}.${entry.date.value}.${level}.${index}`}
              size={size}
              columns={columns}
              entry={_entry}
              index={index}
              level={level + 1}
            />
          ))}
        </Box>
      )}
    </VStack>
  );
};
